import React, { useContext, useState } from "react";
import { useNavigate } from 'react-router-dom';
import PropTypes from "prop-types";
import _ from "lodash";
import moment from "moment";

import { isSubmitBigWin, isCashout, isCopy, isDetail, isPay } from "@tickets/helpers/actions/isActionAvailable";
import { getTicketStateName } from "@tickets/helpers/ticketState";
import { isTicketPayPeriodExpired, payTicket } from "@tickets/components/cardView/helper";
import { earlyCashout } from "@tickets/helpers/actions/cashout";
import { getTicketIndex } from "@tickets/helpers/actions/pay";
import ActionSquare from "@common/components/actionSquare";
import ToastWrapper from "@common/helpers/toastWrapper";
import { PopupContext } from "@app/dev/contexts/popup";
import { POPUPS } from "@common/constants/popups";
import useTicketsSync from "@tickets/hooks";
import { copyToBetslip } from "@tickets/helpers/actions/copyToBetslip";
import { openDeferModal } from "@tickets/helpers/openDeferPopup";
import { getCasinoCopyBetData } from "@tickets/helpers/mappers";

const CardActions = ({ ticket, onCashout, setTickets, tickets, winAmount, cashoutAmount, onCashoutAmountChange }) => {
	const [{ openPopup, closePopup }] = useContext(PopupContext);
	const [loading, setLoading] = useState(false);
	const [isCashoutLoading, setIsCashoutLoading] = useState(false);
	const [canPayTicket, setCanPayTicket] = useState(true);
	const navigate = useNavigate();

	const { isPayable } = getTicketStateName(ticket);

	const getBarcode = s => s.barcode?.toString().slice(0, -1) || s.dataFromSwarm?.barcode?.toString();
	const { ticketType } = ticket;
	const activeRowIndex = tickets[ticketType]?.findIndex(item => getBarcode(item) === getBarcode(ticket));

	useTicketsSync(activeRowIndex, onCashoutAmountChange, setLoading);

	const sportTicketCashoutAmount = ticket?.dataFromSwarm?.is_live
		? cashoutAmount || 0
		: ticket?.dataFromSwarm?.cashout_amount || 0;

	const cashout = async () => {
		if (isCashoutLoading) return;

		// openPopup(POPUPS.PROCESS_POPUP, {
		// 	title: "Cashout in process",
		// 	icon: "cashout",
		// 	content: `Cashout is in progress. Please wait while the process is completed. Note that the cashout amount may change depending on the current odds and conditions.`,
		// });

		setIsCashoutLoading(true);
		const cashoutResult = await earlyCashout(ticket, sportTicketCashoutAmount);

		if (!cashoutResult) {
			setIsCashoutLoading(false);
			return;
		}

		const { success, newPrice } = cashoutResult;

		if (success) {
			ToastWrapper("success", "Successfully cashout");
			onCashout?.(ticket);
		} else if (newPrice) {
			ToastWrapper("error", `Cashout amount updated to ${newPrice}`);
			onCashout?.(ticket, newPrice);
		} else {
			ToastWrapper("error", "Cashout bet failed!");
		}

		closePopup();
		setTimeout(() => {
			setIsCashoutLoading(false);
		}, 2000);
	};

	const submitBigWin = async () => {
		if (isTicketPayPeriodExpired(ticket)) return;

		const isSuccess = await openDeferModal(
			POPUPS.REQUEST_PAYOUT,
			{ betId: ticket?.dataFromSwarm?.Id ?? ticket?.dataFromSwarm?.id, popupPosition: "top", isCasino: !!ticket?.dataFromCasino?.TransactionId, winId: ticket?.dataFromSwarm?.WinId, currencyId: ticket?.dataFromSwarm?.currency_id },
			false
		);

		if (!isSuccess) return;

		const ticketIndex = getTicketIndex(ticket, tickets);
		const updatedTickets = [...tickets[ticket.ticketType]];

		updatedTickets[ticketIndex] = { ...ticket, paid: true };
		if (ticketIndex !== -1) {
			setTickets({
				...tickets,
				[ticket.ticketType]: updatedTickets,
			});
		}
	};

	const handleCopy = (ticket) => {
		if (ticket.dataFromCasino) {
			const copyData = getCasinoCopyBetData({ casinoInfo: ticket.dataFromCasino });
			if (copyData?.gameId) {
				navigate(`/thirdPartyGame/${copyData.gameId}?action=copyTicket&betId=${ticket.dataFromCasino.CasinoTransactionId}`);
				closePopup();
			}
			return;
		}

		return copyToBetslip(ticket);
	};

	const addBalance = async () => {
		if (isTicketPayPeriodExpired(ticket)) return;
		// make call per 2 seconds
		if (loading || !canPayTicket) return;

		setLoading(true);
		setCanPayTicket(false);

		setTimeout(() => {
			setCanPayTicket(true);
		}, 2000); // fix for ddosing button

		openPopup(POPUPS.PROCESS_POPUP, {
			title: "Pay in process",
			icon: "pay",
			content: "Pay is in progress. Please wait while the process is completed.",
		});

		const [data] = await payTicket([ticket]);

		if (data?.Result?.Succeeded) {
			const ticketIndex = getTicketIndex(ticket, tickets);
			if (ticketIndex !== -1) {
				const updatedTickets = [...tickets[ticket.ticketType]];
				updatedTickets[ticketIndex] = { ...ticket, paid: true, isChecked: false };

				setTickets({
					...tickets,
					[ticket.ticketType]: updatedTickets,
				});
			}
			ToastWrapper("success", "Successfully add to balance");
		} else if (data) {
			ToastWrapper("error", data?.Result?.Message);
		}

		closePopup();
		setLoading(false);
	};

	return (
		<div className="ticket-section ticket-action-container">
			{isDetail(ticket) && (
				<ActionSquare
					actionName="details"
					onClick={() =>
						openPopup(POPUPS.TICKET_DETAILS_POPUP, {
							data: ticket,
							isDisable: true,
							ticketType,
						})
					}
					icon="icon-booklet"
				/>
			)}
			{isCopy(ticket) && (
				<ActionSquare
					actionName="copy"
					onClick={() => handleCopy(ticket)}
					icon="icon-action-copy"
				/>
			)}
			{isCashout(ticket) && !!sportTicketCashoutAmount && (
				<ActionSquare
					actionName={["cashout {1}", sportTicketCashoutAmount]}
					icon="icon-pay"
					onClick={cashout}
					className="detail-cashout"
					isLoading={isCashoutLoading}
				/>
			)}
			{isPay(ticket, isPayable, winAmount) && (
				<ActionSquare
					actionName={["pay {1}", winAmount]}
					className="detail-cashout"
					icon="icon-pay"
					onClick={addBalance}
				/>
			)}
			{isSubmitBigWin(ticket, isPayable, winAmount) && (
				<ActionSquare
					actionName="Submit payout request"
					icon="icon-pay"
					onClick={submitBigWin}
					className="payout-request"
				/>
			)}
		</div>
	);
};

export default CardActions;

CardActions.propTypes = {
	ticket: PropTypes.object,
	tickets: PropTypes.object,
	setTickets: PropTypes.func,
	cashoutAmount: PropTypes.number,
	onCashoutAmountChange: PropTypes.func,
};
